import BaseDrawer from "@stardust/base-drawer";
import { initAccordion } from "@stardust/accordion";
import { prefersReducedMotion } from "@stardust/utilities";

/**
 * PDP Info Drawer (aggregated, keyed)
 *
 * Drawer-mode `pdp-info` blocks each emit a hidden
 * `<template data-pdp-info-panel="{blockId}" data-pdp-info-key="{key}">` holding a
 * `<details>` panel. Blocks sharing a key open the same drawer; "none"-group blocks
 * use their own block id as the key, so each gets a solo drawer.
 *
 * The manager provisions one drawer per distinct key by cloning a single `<template>`
 * shell (snippets/pdp-info-drawer.liquid, rendered once outside the section) and
 * appending it to <body>. Because keys are discovered at runtime, adding a new group
 * is a schema-only change — no markup or JS edits. Panels are re-synced on
 * `shopify:section:load` / `theme:section:morph`; drawers whose key disappears are
 * torn down.
 */
class PdpInfoDrawer extends BaseDrawer {
  constructor(element) {
    super(element, {
      openClass: 'opacity-0 translate-x-full',
      closeClass: 'opacity-0 translate-x-full',
      animationDuration: 300,
      overlayZIndex: 10000,
      lockScroll: true,
    });
    this.panelsContainer = element.querySelector('[data-pdp-info-panels]');
    this.pendingTemplates = null; // rebuild deferred while open, applied on next open
  }

  load() {
    const closeBtn = this.element.querySelector('[data-pdp-info-drawer-close]');
    if (closeBtn) closeBtn.addEventListener('click', this._boundClose);
  }

  /**
   * Rebuild this drawer's exclusive accordion from its panel templates. Rebuilt
   * wholesale so morph content changes are picked up and no duplicate listeners
   * accumulate (initAccordion binds per `<details>`; discarding old nodes discards them).
   * @param {HTMLTemplateElement[]} templates
   */
  setPanels(templates) {
    if (!this.panelsContainer) return;
    this.panelsContainer.replaceChildren();
    templates.forEach((tpl) => {
      const panel = tpl.content.firstElementChild?.cloneNode(true);
      if (!panel) return;
      panel.setAttribute('data-pdp-info-panel-id', tpl.getAttribute('data-pdp-info-panel'));
      this.panelsContainer.appendChild(panel);
    });
    initAccordion(this.panelsContainer, { exclusive: true });
  }

  /**
   * Open the drawer, expand the requested panel, collapse the rest, scroll it in.
   * @param {string} panelId
   */
  openTo(panelId) {
    // Apply a rebuild that was deferred while the drawer was open (morph mid-session).
    // Only when actually closed right now — re-opening to a different panel while
    // still open must not wipe the panels and drop trapped focus.
    if (this.pendingTemplates && !this.isOpen) {
      this.setPanels(this.pendingTemplates);
      this.pendingTemplates = null;
    }
    this.open();
    let target = null;
    this.panelsContainer?.querySelectorAll('[data-pdp-info-panel-id]').forEach((panel) => {
      const match = panel.getAttribute('data-pdp-info-panel-id') === panelId;
      panel.open = match;
      if (match) target = panel;
    });
    if (target) {
      const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      requestAnimationFrame(() => target.scrollIntoView({ block: 'nearest', behavior }));
    }
  }

  destroy() {
    super.destroy();
    this.element.remove();
  }
}

/**
 * Provisions one PdpInfoDrawer per distinct panel key, keeps them in sync with the
 * panel templates on the page, and routes `pdp-info:open` events by key.
 */
class PdpInfoDrawerManager {
  /** @param {HTMLTemplateElement} shell - The `<template>` drawer prototype */
  constructor(shell) {
    this.shell = shell;
    this.drawers = new Map(); // key -> PdpInfoDrawer
    this._sync = this._sync.bind(this);
    this._handleOpen = this._handleOpen.bind(this);
  }

  init() {
    this._sync();
    // Triggers dispatch `pdp-info:open` via theme.js's data-triggers-event delegation.
    document.addEventListener('pdp-info:open', this._handleOpen);
    document.addEventListener('shopify:section:load', this._sync);
    document.addEventListener('theme:section:morph', this._sync);
  }

  /** Reconcile drawers against the current panel templates: create, refill, reap. */
  _sync() {
    // The shell node is replaced wholesale by section re-renders (shopify:section:load /
    // theme:section:morph) — re-fetch it so new drawers clone current markup and existing
    // drawers pick up a changed heading instead of keeping the stale one forever.
    const currentShell = document.querySelector('[data-pdp-info-drawer-template]');
    if (currentShell) this.shell = currentShell;
    const shellHeading = this.shell.content.querySelector('h2');
    if (shellHeading) {
      this.drawers.forEach((drawer) => {
        const heading = drawer.element.querySelector('h2');
        if (heading && heading.textContent !== shellHeading.textContent) {
          heading.textContent = shellHeading.textContent;
        }
      });
    }

    const byKey = new Map();
    document.querySelectorAll('template[data-pdp-info-panel][data-pdp-info-key]').forEach((tpl) => {
      const key = tpl.getAttribute('data-pdp-info-key');
      if (!key) return;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(tpl);
    });

    byKey.forEach((templates, key) => {
      let drawer = this.drawers.get(key);
      if (!drawer) {
        drawer = this._createDrawer(key);
        if (!drawer) return;
        this.drawers.set(key, drawer);
      }
      // Don't rebuild an open drawer — replaceChildren would collapse the expanded
      // panel and drop trapped focus mid-interaction. Stash it and rebuild on next open.
      if (drawer.isOpen) {
        drawer.pendingTemplates = templates;
      } else {
        drawer.pendingTemplates = null;
        drawer.setPanels(templates);
      }
    });

    // Tear down drawers whose key no longer has any panels (block removed / hidden).
    this.drawers.forEach((drawer, key) => {
      if (!byKey.has(key)) {
        try {
          drawer.destroy();
        } catch (e) {
          console.warn(`[PdpInfoDrawer] Error destroying drawer "${key}":`, e);
        }
        this.drawers.delete(key);
      }
    });
  }

  /**
   * Clone the shell, tag it with the key, append to <body>, and wire it up.
   * @param {string} key
   * @returns {PdpInfoDrawer}
   */
  _createDrawer(key) {
    const first = this.shell.content.firstElementChild;
    if (!first) return null;
    const el = first.cloneNode(true);
    el.setAttribute('data-pdp-info-key', key);
    // Label the dialog via its heading (aria-labelledby stays in sync with the visible title).
    const heading = el.querySelector('h2');
    if (heading) {
      const titleId = `pdp-info-drawer-title-${key}`;
      heading.id = titleId;
      el.setAttribute('aria-labelledby', titleId);
    }
    document.body.appendChild(el);
    const drawer = new PdpInfoDrawer(el);
    drawer.load();
    return drawer;
  }

  /** @param {CustomEvent} e - `pdp-info:open` with detail.key and detail.panelId */
  _handleOpen(e) {
    const key = e.detail?.key;
    if (key) this.drawers.get(key)?.openTo(e.detail?.panelId);
  }
}

const shell = document.querySelector('[data-pdp-info-drawer-template]');
if (shell && !shell.__pdpInfoManager) {
  shell.__pdpInfoManager = new PdpInfoDrawerManager(shell);
  shell.__pdpInfoManager.init();
}
